'use client'

import Link from 'next/link'
import Script from 'next/script'
import { useTranslations } from 'next-intl'
import { useEffect, useState } from 'react'

type ConsentChoice = 'accepted' | 'rejected'

type ConsentManagedScriptsProps = {
  // AdSense 暂停接入：恢复时取消注释 adsenseClient 和下方脚本块。
  // adsenseClient?: string
  googleTrackingId?: string
}

const CONSENT_STORAGE_KEY = 'snapbracket:cookie-consent'
const TINYADZ_SITE_ID = '6a4f1a290c2a60ac0ca917b0'

function readStoredConsent(): ConsentChoice | null {
  try {
    const stored = window.localStorage.getItem(CONSENT_STORAGE_KEY)
    return stored === 'accepted' || stored === 'rejected' ? stored : null
  } catch {
    return null
  }
}

function writeStoredConsent(choice: ConsentChoice) {
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, choice)
  } catch {
    // 存储不可用时，仅在当前会话中保留选择。
  }
}

export default function ConsentManagedScripts({
  // adsenseClient,
  googleTrackingId,
}: ConsentManagedScriptsProps) {
  const t = useTranslations('CookieConsent')
  const [choice, setChoice] = useState<ConsentChoice | null>(null)
  const [hasLoadedChoice, setHasLoadedChoice] = useState(false)

  useEffect(() => {
    setChoice(readStoredConsent())
    setHasLoadedChoice(true)
  }, [])

  const setConsent = (nextChoice: ConsentChoice) => {
    writeStoredConsent(nextChoice)
    setChoice(nextChoice)
  }

  const canLoadAnalytics = choice === 'accepted'

  return (
    <>
      {/* 广告独立加载，Cookie 同意选择仅控制 Google Analytics。 */}
      <Script
        id="tinyadz-init"
        src="https://cdn.apitiny.net/scripts/v2.0/main.js"
        strategy="afterInteractive"
        data-site-id={TINYADZ_SITE_ID}
        data-test-mode="false"
        async
      />

      <Script
        id="google-consent-default"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('consent', 'default', {
              ad_storage: 'denied',
              ad_user_data: 'denied',
              ad_personalization: 'denied',
              analytics_storage: 'denied',
              wait_for_update: 500
            });
          `,
        }}
      />

      {canLoadAnalytics && (
        <>
          <Script
            id="google-consent-accepted"
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{
              __html: `
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('consent', 'update', {
                  ad_storage: 'denied',
                  ad_user_data: 'denied',
                  ad_personalization: 'denied',
                  analytics_storage: 'granted'
                });
              `,
            }}
          />

          {/*
            AdSense 暂停接入：恢复时取消注释 adsenseClient prop 和此脚本块。
            {adsenseClient ? (
              <Script
                id="adsbygoogle-init"
                src={`https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${adsenseClient}`}
                strategy="afterInteractive"
                crossOrigin="anonymous"
              />
            ) : null}
          */}

          {googleTrackingId ? (
            <>
              <Script
                id="gtag-loader"
                strategy="afterInteractive"
                src={`https://www.googletagmanager.com/gtag/js?id=${googleTrackingId}`}
              />
              <Script
                id="gtag-init"
                strategy="afterInteractive"
                dangerouslySetInnerHTML={{
                  __html: `
                    window.dataLayer = window.dataLayer || [];
                    function gtag(){dataLayer.push(arguments);}
                    gtag('js', new Date());
                    gtag('config', '${googleTrackingId}', {
                      page_path: window.location.pathname,
                    });
                  `,
                }}
              />
            </>
          ) : null}
        </>
      )}

      {hasLoadedChoice && choice === null ? (
        <div
          role="region"
          aria-label={t('label')}
          className="fixed inset-x-3 bottom-3 z-50 mx-auto max-w-3xl rounded border border-border bg-background p-4 shadow-lg"
        >
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <p className="text-sm text-muted-foreground">
              {t('description')}{' '}
              <Link
                href="/privacy-policy"
                className="font-medium text-foreground underline underline-offset-4"
              >
                {t('privacyPolicy')}
              </Link>
            </p>
            <div className="flex shrink-0 flex-wrap justify-end gap-2">
              <button
                type="button"
                className="rounded border border-border px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted"
                onClick={() => setConsent('rejected')}
              >
                {t('reject')}
              </button>
              <button
                type="button"
                className="rounded bg-foreground px-3 py-2 text-sm font-medium text-background transition-colors hover:bg-foreground/90"
                onClick={() => setConsent('accepted')}
              >
                {t('accept')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  )
}
